import React, { useEffect, useState, useContext as useContextCore, useMemo } from 'react'
import merge from 'lodash/merge'
import { components as defaultComponents } from './components'
import { useListWithSelection } from '@ztechapps/common.hook'
import { useLocalListAction } from '@ztechapps/common.hook'
import type { Task } from '@ztechapps/common.contract'
import { taskDocumentQueries } from '@ztechapps/task.common'
import type { TaskListContextType, TaskListProviderProps } from '@ztechapps/webui.contract'

const context = React.createContext<TaskListContextType>({} as TaskListContextType)
const CoreProvider = context.Provider

export const TaskListProvider = ({
  disabled,
  components,
  children,
  tasks,
  error,
  loading,
  afterUpdate,
  onDelete,
  head,
  taskDetailRoute,
  updateConfig,
  subTaskConfig,
  onDragSort,
  enableReorder,
  scrollable,
}: TaskListProviderProps) => {
  const mergedComponents = useMemo(() => merge({}, defaultComponents, components), [components])

  const resolvedSubTaskConfig = useMemo(
    () => ({
      ...((subTaskConfig ?? {}) as TaskListProviderProps['subTaskConfig']),
      level: subTaskConfig?.level ?? 0,
    }),
    [subTaskConfig]
  )

  const [_loading, setLoading] = useState(loading)
  useEffect(() => {
    setLoading(loading)
  }, [loading])

  const localListAction = useLocalListAction(tasks as Task[], {
    defaultSort: { name: 1 },
  })

  const taskListWithSelection = useListWithSelection(localListAction.list)

  const resolvedUpdateConfig = useMemo(
    () => ({
      //set the fallback to task update config
      query: updateConfig?.query ?? taskDocumentQueries.updateConfig?.query,
      updateParams:
        updateConfig?.updateParams ??
        (({ _id, name, updatedValue }) => {
          return {
            input: {
              _id,
              [name]: updatedValue,
            },
          }
        }),
    }),
    [updateConfig]
  )

  const contextValue = useMemo(
    () => ({
      list: localListAction.list,
      taskListWithSelection,
      error,
      loading: _loading,
      setLoading,
      updateConfig: resolvedUpdateConfig,
      afterUpdate,
      onDelete,
      head,
      taskDetailRoute,
      localListAction,
      disabled,
      components: mergedComponents,
      tasks,
      subTaskConfig: resolvedSubTaskConfig,
      onDragSort,
      enableReorder,
      scrollable,
    }),
    [
      localListAction,
      taskListWithSelection,
      error,
      _loading,
      resolvedUpdateConfig,
      afterUpdate,
      onDelete,
      head,
      taskDetailRoute,
      disabled,
      mergedComponents,
      tasks,
      resolvedSubTaskConfig,
      onDragSort,
      enableReorder,
      scrollable,
    ]
  )

  return <CoreProvider value={contextValue}>{children}</CoreProvider>
}

export const useTaskListContext = () => useContextCore(context)
